// Media helpers: read files as base64 and capture the first frame of a video.

// Read a video file's duration (in seconds). Resolves 0 if it can't be read.
export const getVideoDuration = (file) =>
  new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      video.src = url;
      video.onloadedmetadata = () => {
        const d = video.duration && isFinite(video.duration) ? video.duration : 0;
        URL.revokeObjectURL(url);
        resolve(d);
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(0);
      };
      setTimeout(() => resolve(video.duration || 0), 5000);
    } catch (e) {
      resolve(0);
    }
  });

export const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Capture several evenly-spaced frames of a video as small JPEG data URLs (animated teaser source).
export const captureVideoFrames = (file, count = 5, maxW = 320) =>
  new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "auto";
      video.muted = true;
      video.playsInline = true;
      video.src = url;
      const frames = [];
      let times = [];
      let idx = 0;
      const cleanup = () => URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");

      const finish = () => { cleanup(); resolve(frames); };
      const grab = () => {
        try {
          const w = video.videoWidth || maxW;
          const h = video.videoHeight || maxW;
          const scale = Math.min(1, maxW / Math.max(1, w));
          canvas.width = Math.max(1, Math.round(w * scale));
          canvas.height = Math.max(1, Math.round(h * scale));
          const ctx = canvas.getContext("2d");
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          frames.push(canvas.toDataURL("image/jpeg", 0.6));
        } catch (e) {}
        idx += 1;
        if (idx < times.length) {
          try { video.currentTime = times[idx]; } catch (e) { finish(); }
        } else {
          finish();
        }
      };

      video.onloadedmetadata = () => {
        const dur = video.duration && isFinite(video.duration) ? video.duration : 1;
        times = Array.from({ length: count }, (_, i) => Math.max(0.05, (dur * (i + 0.5)) / count));
        try { video.currentTime = times[0]; } catch (e) { grab(); }
      };
      video.onseeked = grab;
      video.onerror = () => finish();
      setTimeout(() => { if (frames.length) finish(); }, 8000);
    } catch (e) {
      resolve([]);
    }
  });

// Capture the first frame of a video file as a JPEG data URL (poster/teaser source).
export const captureVideoFrame = (file) =>
  new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      video.playsInline = true;
      video.src = url;

      const cleanup = () => URL.revokeObjectURL(url);

      const grab = () => {
        try {
          const canvas = document.createElement("canvas");
          const w = video.videoWidth || 640;
          const h = video.videoHeight || 800;
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(video, 0, 0, w, h);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
          cleanup();
          resolve(dataUrl);
        } catch (e) {
          cleanup();
          resolve(null);
        }
      };

      video.onloadeddata = () => {
        // seek slightly in to ensure a decoded frame is available
        try {
          video.currentTime = Math.min(0.1, (video.duration || 1) / 2);
        } catch (e) {
          grab();
        }
      };
      video.onseeked = grab;
      video.onerror = () => {
        cleanup();
        resolve(null);
      };
    } catch (e) {
      resolve(null);
    }
  });
